import { toast } from "react-toastify";
import type { IBaseRestService, TDataFormat } from "./base-rest-service-types";
import { ServerError } from "./server-error";
import { getFilename } from "../../utils/get-file-name";

export class BaseRestService implements IBaseRestService {
    constructor(public baseUrl: string) {
    }

    private async fetch<T>({
                               url,
                               request,
                               options,
                               method,
                               file,
                               files,
                               filesFieldName
                           }: {
        url: string;
        file?: File;
        files?: File[];
        filesFieldName?: string;
        request?: Record<string, unknown>;
        options?: RequestInit;
        method: string;
    }): Promise<T> {
        return BaseRestService.fetch<T>({
            url: `${this.baseUrl}${url}`,
            request,
            options,
            method,
            file,
            files,
            filesFieldName
        });
    }

    static async fetch<T>({
                              url,
                              request,
                              options,
                              method,
                              file,
                              files,
                              filesFieldName,
                              dataFormat = "formData"
                          }: {
        url: string;
        request?: Record<string, unknown>;
        file?: File;
        files?: File[];
        filesFieldName?: string;
        dataFormat?: TDataFormat;
        options?: RequestInit;
        method: string;
    }): Promise<T> {
        let body;
        let requestOptions = { ...options };
        if (dataFormat === "formData") {
            const formData = new FormData();
            if (request) {
                Object.entries(request).forEach(([key, value]) => {
                    if (Array.isArray(value)) {
                        value.forEach((item) => {
                            formData.append(key, item as string | Blob);
                        });
                    } else {
                        if (value !== undefined && value !== null)
                            formData.append(key, value as string | Blob);
                    }
                })
            }
            if (file) {
                formData.append("file", file)
            }
            if (files && filesFieldName) {
                files.forEach((f) => {
                    formData.append(filesFieldName, f)
                })
            }
            body = formData;
        }

        if (dataFormat === "json") {
            body = JSON.stringify(request);
            if (requestOptions) {
                requestOptions = {
                    ...requestOptions, headers: {
                        ...requestOptions.headers,
                        "Accept": "application/json",
                        "Content-Type": "application/json"
                    }
                }
            } else {
                requestOptions = {
                    headers: {
                        "Accept": "application/json",
                        "Content-Type": "application/json"
                    }
                }
            }
        }

        const response = await fetch(url, {
            method,
            body: body,
            ...requestOptions,
        });
        let isError = false;
        if (!response.ok) {
            isError = true;
        }
        let result: T;

        if (response.headers.get("content-type")?.includes("application/pdf") ||
            response.headers.get("content-type")?.includes("application/vnd.openxmlformats-officedocument.wordprocessingml.document") ||
            response.headers.get("content-type")?.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")) {
            const blob = await response.blob();
            const file = await new Response(blob).arrayBuffer().then(buffer => {
                return new Uint8Array(buffer);
            });
            const filename = getFilename(response.headers.get("content-disposition"));
            result = { file: file, blob, filename } as T;
        } else {
            if (response.headers.get("content-type")?.includes("application/json")) {
                result = await response.json();
            } else {
                result = (await response.text()) as any as T;
            }
        }

        if (isError) {
            throw new ServerError({
                status: response.status,
                message: response.statusText,
                response: result,
            });
        }
        return result;
    }

    async get<T>({
                     url,
                     options,
                     responseOptions,
                     request,
                 }: {
        url: string;
        responseOptions?: Record<string, string>,
        request?: Record<string, string>;
        options?: RequestInit;
    }): Promise<T> {
        const fullUrl = new URL(`${this.baseUrl}/${url}`);
        if (request) {
            Object.entries(request).forEach(([key, value]) => {
                if (value !== undefined && value !== null)
                    fullUrl.searchParams.append(key, value);
            });
        }
        return BaseRestService.get(fullUrl.toString(), options, responseOptions);
    }

    static async get<T>(url: string, options?: RequestInit, responseOptions?: Record<string, string>): Promise<T> {
        const response = await fetch(url, options);

        if (response.ok && responseOptions?.responseType === "file") {
            return (await response.blob()) as T;
        }

        let isError = false;
        if (!response.ok) {
            isError = true;
        }
        let result: T;

        if (response.headers.get("content-type")?.includes("application/pdf") ||
            response.headers.get("content-type")?.includes("application/vnd.openxmlformats-officedocument.wordprocessingml.document") ||
            response.headers.get("content-type")?.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")) {
            const blob = await response.blob();
            const file = await new Response(blob).arrayBuffer().then(buffer => {
                return new Uint8Array(buffer);
            });
            const filename = getFilename(response.headers.get("content-disposition"));
            result = { file: file, blob, filename } as T;
        } else {
            if (response.headers.get("content-type")?.includes("application/json")) {
                result = await response.json();
            } else {
                result = (await response.text()) as any as T;
            }
        }

        if (isError) {
            throw new ServerError({
                status: response.status,
                message: response.statusText,
                response: result,
            });
        }
        return result;
    }

    async post<T>(args: {
        url: string;
        file?: File;
        files?: File[];
        filesFieldName?: string;
        dataFormat?: TDataFormat;
        request?: Record<string, unknown>;
        options?: RequestInit;
    }): Promise<T> {
        return BaseRestService.post<T>({
            ...args,
            url: `${this.baseUrl}/${args.url}`,
        });
    }

    static async post<T>({
                             url,
                             file,
                             files,
                             filesFieldName,
                             dataFormat,
                             request,
                             options,
                         }: {
        url: string;
        file?: File;
        files?: File[];
        filesFieldName?: string;
        dataFormat?: TDataFormat;
        request?: Record<string, unknown>;
        options?: RequestInit;
    }): Promise<T> {
        return BaseRestService.fetch<T>({
            url,
            request,
            options,
            file,
            files,
            filesFieldName,
            dataFormat,
            method: "POST",
        });
    }

    async put<T>(args: {
        url: string;
        request?: Record<string, unknown>;
        options?: RequestInit;
    }): Promise<T> {
        return BaseRestService.put<T>({
            ...args,
            url: `${this.baseUrl}/${args.url}`,
        });
    }

    static async put<T>({
                            url,
                            request,
                            options,
                        }: {
        url: string;
        request?: Record<string, unknown>;
        options?: RequestInit;
    }): Promise<T> {
        return BaseRestService.fetch<T>({
            url,
            request,
            options,
            method: "PUT",
        });
    }

    async patch<T>({
                       url,
                       request,
                       options,
                   }: {
        url: string;
        request?: Record<string, unknown>;
        options?: RequestInit;
    }): Promise<T> {
        return this.fetch({
            url,
            request,
            options,
            method: "PATCH",
        });
    }

    async delete<T>(args: { url: string; options?: RequestInit; request?: Record<string, string>; }): Promise<T> {
        const fullUrl = new URL(`${this.baseUrl}/${args.url}`);
        if (args.request) {
            Object.entries(args.request).forEach(([key, value]) => {
                fullUrl.searchParams.append(key, value);
            });
        }
        return BaseRestService.delete<T>({
            ...args,
            url: fullUrl.toString(),
        });
    }

    static async delete<T>({
                               url,
                               request,
                               options,
                           }: {
        url: string;
        request?: Record<string, unknown>;
        options?: RequestInit;
    }): Promise<T> {
        return BaseRestService.fetch<T>({
            url,
            options,
            method: "DELETE",
        });
    }
}
